import { DatePipe } from '@angular/common';
import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { DocumentController } from '@domain';
import {
    RaButtonComponent,
    RaDialogConfirmationService,
    RaLoadingComponent,
    RaToastService,
} from '@shared/components';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TrashDocumentItemController } from 'src/app/domain/controllers/trash-document-item.controller';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaEmptyComponent } from 'src/app/shared/components/ra-empty/ra-empty.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { CardTypeFormatPipe } from 'src/app/shared/pipes/card-type-format.pipe';
import { EditDataService } from '../../../services/_edit-data.service';
import { MenuOptionsService } from '../../../services/menu-options.service';
import { CardTypes } from '../../cards/_card-base/enums/card-types.enum';
import { CalloutCardComponent } from '../../cards/callout-card/callout-card.component';
import { StatsCardComponent } from '../../cards/stats-card/stats-card.component';
import { TableCardComponent } from '../../cards/table-card/table-card.component';
import { SemaphoreCardComponent } from '../../cards/semaphore-card/semaphore-card.component';
import { ChartCardComponent } from '../../cards/chart-card/chart-card.component';
import { GroupImageCardComponent } from '../../cards/group-image-card/group-image-card.component';
import { ImageCardComponent } from '../../cards/image-card/image-card.component';
import { TextCardComponent } from '../../cards/text-card/text-card.component';
import { VideoCardComponent } from '../../cards/video-card/video-card.component';

@Component({
    selector: 'modal-list-trash',
    templateUrl: 'modal-list-trash.component.html',
    styleUrl: 'modal-list-trash.component.scss',
    imports: [
        RaModalComponent,
        RaLoadingComponent,
        RaEmptyComponent,
        TextCardComponent,
        ImageCardComponent,
        VideoCardComponent,
        RaButtonComponent,
        GroupImageCardComponent,
        CalloutCardComponent,
        StatsCardComponent,
        TableCardComponent,
        SemaphoreCardComponent,
        ChartCardComponent,
        TranslatePipe,
    ],
    providers: [DatePipe, CardTypeFormatPipe],
})
export class ModalListTrashComponent implements OnInit {
    public documentId = input.required<string>();
    public close = output<void>();
    public loading = signal(false);
    public loadingRestoreField = signal(false);
    public trashDocumentItems = signal<TrashDocumentItemEntity[]>([]);
    public itemsSelected = signal<TrashDocumentItemEntity[]>([]);
    public cardTypes = CardTypes;

    // * [Injects]
    private _datePipe = inject(DatePipe);
    private _raToastService = inject(RaToastService);
    private _editDataService = inject(EditDataService);
    private _documentController = inject(DocumentController);
    private _cardTypeFormatPipe = inject(CardTypeFormatPipe);
    private _menuOptionsService = inject(MenuOptionsService);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _trashDocumentItemController = inject(TrashDocumentItemController);
    private _translate = inject(TranslateService);

    ngOnInit(): void {
        this.getTrashItems();
    }

    /**
     * Obtém os itens da lixeira
     */
    private getTrashItems(): void {
        this.loading.set(true);
        this._trashDocumentItemController.getAllByDocumentId(this.documentId()).subscribe({
            next: trashDocumentItems => {
                this.trashDocumentItems.set(trashDocumentItems);
                this._processData();
                this.loading.set(false);
            },
            error: () => this.loading.set(false),
        });
    }

    /**
     * Processa os dados dos itens da lixeira
     */
    private _processData(): void {
        for (const trashDocumentItem of this.trashDocumentItems()) {
            const sessionName = trashDocumentItem.sessionName;
            const itemType = this._translate.instant(
                this._cardTypeFormatPipe.transform(trashDocumentItem.item)
            );
            trashDocumentItem.item.name = `${sessionName} - ${itemType}`;
        }
    }

    /**
     * Seleciona um item
     * @param item Item to select
     */
    public selectItem(item: TrashDocumentItemEntity): void {
        const existItem = this.itemsSelected().find(i => i._id === item._id);
        if (existItem) {
            this.itemsSelected.set(this.itemsSelected().filter(i => i._id !== item._id));
        } else {
            this.itemsSelected.set([...this.itemsSelected(), item]);
        }
    }

    /**
     * Verifica se um item está selecionado
     * @param item Item to check
     * @returns True if item is selected, false otherwise
     */
    public isItemSelected = (item: TrashDocumentItemEntity) =>
        this.itemsSelected().find(i => i._id === item._id) !== undefined;

    /**
     * Restaura itens da lixeira
     */
    public restoreItems(): void {
        if (this.loadingRestoreField()) return;

        if (this.itemsSelected().length === 0) {
            this._raToastService.error({
                message: this._translate.instant('document.edit.listTrash.noneSelectedRestore'),
            });
            return;
        }

        this.loadingRestoreField.set(true);
        this._trashDocumentItemController
            .restore(
                this.documentId(),
                this.itemsSelected().map(i => i._id)
            )
            .subscribe({
                next: () => this.close.emit(),
                error: () => this.loadingRestoreField.set(false),
            });
    }

    /**
     * Deleta permanentemente itens da lixeira
     */
    public deletePermanentlyItems(): void {
        if (this.itemsSelected().length === 0) {
            this._raToastService.error({
                message: this._translate.instant('document.edit.listTrash.noneSelectedDelete'),
            });
            return;
        }

        // Deletar permanentemente os itens selecionados
        this._trashDocumentItemController
            .deletePermanently(
                this.documentId(),
                this.itemsSelected().map(i => i._id)
            )
            .subscribe({
                next: () => this.close.emit(),
            });
    }
}
